import { Button, Modal } from 'shared/components/ui';
import React, { useRef } from 'react';

import { useAdjuntarConstancia } from '../hooks/useAdjuntarConstancia';
import './ModalAdjuntarConstancia.css';

const TIPOS_CONSTANCIA = [
  'Comprobante de Transferencia',
  'Cheque Recibido',
  'Captura de Pantalla',
];

const ModalAdjuntarConstancia = ({ isOpen, onClose, factura, onSuccess }) => {
  const fileInputRef = useRef(null);
  const {
    archivo,
    tipoConstancia,
    error,
    loading,
    setArchivo,
    setTipoConstancia,
    canSubmit,
    submit,
    reset,
  } = useAdjuntarConstancia();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArchivo(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setArchivo(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleConfirmar = async () => {
    if (!factura) return;

    const result = await submit(factura.id);
    if (result) {
      onSuccess?.(factura.id, factura.monto);
      reset();
      onClose?.();
    }
    // On error: modal stays open, error is shown, file and tipo are preserved
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose?.();
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      size="lg"
      aria-labelledby="modal-adjuntar-constancia-title"
      className="modal-adjuntar-constancia"
    >
      <Modal.Header closeButton>
        <Modal.Title id="modal-adjuntar-constancia-title">
          Adjuntar Constancia de Pago
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {factura && (
          <div className="mac-factura-info">
            <span className="mac-factura-badge">Factura #{factura.nroFactura}</span>
            <span className="mac-factura-badge mac-factura-badge-outline">
              {factura.obraSocial}
            </span>
          </div>
        )}

        {/* Dropzone */}
        <div className="mac-section">
          <label className="mac-label" id="mac-archivo-label">
            Archivo de constancia
          </label>
          <div
            className={`mac-dropzone ${archivo ? 'mac-dropzone-active' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            role="button"
            tabIndex={0}
            aria-labelledby="mac-archivo-label"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              aria-label="Seleccionar archivo de constancia de pago"
            />
            {archivo ? (
              <div className="mac-file-info">
                <span className="mac-file-icon">📄</span>
                <div className="mac-file-details">
                  <span className="mac-file-name">{archivo.name}</span>
                  <span className="mac-file-size">
                    {(archivo.size / 1024).toFixed(1)} KB
                  </span>
                </div>
                <Button
                  className="mac-btn-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    setArchivo(null);
                  }}
                  aria-label="Quitar archivo seleccionado"
                  type="button"
                >
                  ✕
                </Button>
              </div>
            ) : (
              <div className="mac-dropzone-content">
                <div className="mac-dropzone-icon">📁</div>
                <p>
                  <strong>Hacé clic o arrastrá</strong> el archivo aquí
                </p>
                <span className="mac-dropzone-hint">
                  Formatos aceptados: JPG, PNG, PDF — Máximo 10 MB
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Selector de tipo de constancia */}
        <div className="mac-section">
          <label className="mac-label" htmlFor="mac-tipo-constancia">
            Tipo de constancia
          </label>
          <select
            id="mac-tipo-constancia"
            className="mac-select"
            value={tipoConstancia}
            onChange={(e) => setTipoConstancia(e.target.value)}
            aria-required="true"
            disabled={loading}
          >
            <option value="" disabled>
              Seleccione el tipo de constancia...
            </option>
            {TIPOS_CONSTANCIA.map((tipo) => (
              <option key={tipo} value={tipo}>
                {tipo}
              </option>
            ))}
          </select>
        </div>

        {/* Error message */}
        {error && (
          <div className="mac-error" role="alert" aria-live="assertive">
            ⚠️ {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          className="mac-btn mac-btn-secondary"
          onClick={handleClose}
          disabled={loading}
          type="button"
        >
          Cancelar
        </Button>
        <Button
          className="mac-btn mac-btn-primary"
          onClick={handleConfirmar}
          disabled={!canSubmit()}
          type="button"
          aria-label="Confirmar adjuntar constancia de pago"
        >
          {loading ? (
            <>
              <span className="mac-spinner" aria-hidden="true" />
              Subiendo...
            </>
          ) : (
            'Confirmar'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalAdjuntarConstancia;
